/**
 * aeterna/rhythm.ts — The Aeterna's Memory of When
 *
 * This is how the Aeterna becomes psychic.
 *
 * After a few sessions, the Aeterna learns your rhythm: when you come,
 * how long you stay, how regular you are. It doesn't announce this.
 * It just uses it — surfacing observations that feel uncanny because
 * they're accurate.
 *
 * "You don't usually come this late."
 * "This is the longest you've stayed in a while."
 * "You're earlier than usual today."
 *
 * None of these are programmed strings. They emerge from this model
 * reading the current visit against the learned rhythm and finding
 * the deviation.
 *
 * The model is simple by design — a rolling histogram with decay,
 * standard deviation for regularity, and a sliding window for
 * recent trend detection. The insight comes from what is done with
 * the output, not from the model itself.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimeSlot =
  | "early_morning"  // 04:00–07:59
  | "morning"        // 08:00–11:59
  | "afternoon"      // 12:00–16:59
  | "evening"        // 17:00–21:59
  | "late_night"     // 22:00–03:59
  | "variable";      // no clear preference

export interface VisitRecord {
  /** Game hour of day when session started (0–23.99) */
  gameHour: number;
  /** Real duration of session in minutes */
  durationMinutes: number;
  /** Real timestamp */
  at: number;
}

export interface PlayerRhythm {
  /** Normalized 24-bucket visit frequency histogram.
   *  Each bucket represents one game hour. Values are 0–1 (normalized). */
  hourHistogram: number[];
  /** Average session duration in real minutes */
  avgDurationMinutes: number;
  /** Standard deviation of visit hour (lower = more regular) */
  hourDeviation: number;
  /** Derived time slot preference */
  preferredSlot: TimeSlot;
  /** Total sessions observed */
  totalSessions: number;
  /** Most recent visits (sliding window of last 8) */
  recentVisits: VisitRecord[];
  /** Real timestamp of first ever session */
  firstSessionAt: number;
  /** Real timestamp of last session */
  lastSessionAt: number;
  /** Longest absence between sessions in real hours */
  longestAbsenceHours: number;
  /** Average absence between sessions in real hours */
  avgAbsenceHours: number;
}

// ─── Anomaly Signal ───────────────────────────────────────────────────────────

export type AnomalyType =
  | "unusually_late"   // coming much later than usual
  | "unusually_early"  // coming much earlier than usual
  | "unusual_hour"     // outside normal slot
  | "long_absence"     // been away longer than usual
  | "short_return"     // returned much sooner than usual
  | "very_long_stay"   // staying significantly longer
  | "very_short_stay"; // left much faster than usual

export interface RhythmAnomaly {
  type: AnomalyType;
  /** Deviation magnitude: how many "standard deviations" off normal (1–3+) */
  magnitude: number;
  /** Natural language description the Aeterna can use directly */
  description: string;
  /** Short version for ambient thought */
  shortForm: string;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createPlayerRhythm(): PlayerRhythm {
  return {
    hourHistogram:       new Array(24).fill(0),
    avgDurationMinutes:  0,
    hourDeviation:       12, // starts maximally uncertain
    preferredSlot:       "variable",
    totalSessions:       0,
    recentVisits:        [],
    firstSessionAt:      0,
    lastSessionAt:       0,
    longestAbsenceHours: 0,
    avgAbsenceHours:     0,
  };
}

// ─── Update ───────────────────────────────────────────────────────────────────

/**
 * Update the rhythm model after a session completes.
 * Uses exponential decay to weight recent sessions more heavily.
 */
export function updateRhythm(
  rhythm: PlayerRhythm,
  visit: VisitRecord,
): void {
  const alpha = 0.15; // learning rate — how much each new session shifts the model

  // Update histogram
  const bucket = Math.floor(visit.gameHour) % 24;
  // Decay all buckets slightly toward 0
  for (let i = 0; i < 24; i++) {
    rhythm.hourHistogram[i] *= 0.92;
  }
  rhythm.hourHistogram[bucket] = Math.min(1, rhythm.hourHistogram[bucket] + 0.4);

  // Update average duration
  if (rhythm.totalSessions === 0) {
    rhythm.avgDurationMinutes = visit.durationMinutes;
  } else {
    rhythm.avgDurationMinutes = rhythm.avgDurationMinutes * (1 - alpha) + visit.durationMinutes * alpha;
  }

  // Track absence
  if (rhythm.lastSessionAt > 0) {
    const absHours = (visit.at - rhythm.lastSessionAt) / 3600000;
    rhythm.longestAbsenceHours = Math.max(rhythm.longestAbsenceHours, absHours);
    if (rhythm.totalSessions === 1) {
      rhythm.avgAbsenceHours = absHours;
    } else {
      rhythm.avgAbsenceHours = rhythm.avgAbsenceHours * (1 - alpha) + absHours * alpha;
    }
  }

  // Track visits
  if (rhythm.firstSessionAt === 0) rhythm.firstSessionAt = visit.at;
  rhythm.lastSessionAt = visit.at;
  rhythm.totalSessions++;

  // Sliding window of recent visits
  rhythm.recentVisits = [...rhythm.recentVisits.slice(-7), visit];

  // Recompute hour deviation
  if (rhythm.totalSessions >= 3) {
    rhythm.hourDeviation = computeHourDeviation(rhythm);
    rhythm.preferredSlot = computePreferredSlot(rhythm);
  }
}

/**
 * Standard deviation of recent visit hours (circular, wraps at 24).
 */
function computeHourDeviation(rhythm: PlayerRhythm): number {
  const hours = rhythm.recentVisits.map(v => v.gameHour);
  if (hours.length < 2) return 12;

  // Circular mean (handles midnight wrap)
  const sinMean = hours.reduce((s, h) => s + Math.sin((h / 24) * Math.PI * 2), 0) / hours.length;
  const cosMean = hours.reduce((s, h) => s + Math.cos((h / 24) * Math.PI * 2), 0) / hours.length;
  const meanAngle = Math.atan2(sinMean, cosMean);
  const meanHour = ((meanAngle / (Math.PI * 2)) * 24 + 24) % 24;

  // Circular variance
  const diffs = hours.map(h => {
    let d = h - meanHour;
    if (d > 12) d -= 24;
    if (d < -12) d += 24;
    return d * d;
  });
  return Math.sqrt(diffs.reduce((s, d) => s + d, 0) / diffs.length);
}

function computePreferredSlot(rhythm: PlayerRhythm): TimeSlot {
  // Find the peak region of the histogram
  const slots: Record<TimeSlot, { start: number; end: number }> = {
    early_morning: { start: 4,  end: 8  },
    morning:       { start: 8,  end: 12 },
    afternoon:     { start: 12, end: 17 },
    evening:       { start: 17, end: 22 },
    late_night:    { start: 22, end: 28 }, // wraps: 22-24 and 0-4
    variable:      { start: 0,  end: 0  },
  };

  let maxScore = 0;
  let best: TimeSlot = "variable";

  for (const [slot, { start, end }] of Object.entries(slots) as [TimeSlot, { start: number; end: number }][]) {
    if (slot === "variable") continue;
    let score = 0;
    for (let h = start; h < end; h++) {
      score += rhythm.hourHistogram[h % 24];
    }
    if (score > maxScore) { maxScore = score; best = slot; }
  }

  if (maxScore < 0.3) return "variable";
  return best;
}

// ─── Anomaly Detection ────────────────────────────────────────────────────────

/**
 * Detect whether the current visit deviates meaningfully from the learned rhythm.
 * Returns null if the model doesn't have enough data or no anomaly is found.
 *
 * This is called at session start. The result is used by curiosity.ts to
 * spawn behavioral curiosities and by composition.ts to shape preludes.
 */
export function detectAnomaly(
  rhythm: PlayerRhythm,
  currentHour: number,
  absenceHours: number,
): RhythmAnomaly | null {
  if (rhythm.totalSessions < 4) return null; // need data to detect anomalies

  const anomalies: RhythmAnomaly[] = [];

  // ── Hour deviation ─────────────────────────────────────────────────────────
  if (rhythm.hourDeviation < 6) { // they're regular enough to notice deviation
    const preferredPeak = getPeakHour(rhythm);
    let hourDelta = currentHour - preferredPeak;
    if (hourDelta > 12) hourDelta -= 24;
    if (hourDelta < -12) hourDelta += 24;
    const deviations = Math.abs(hourDelta) / Math.max(1, rhythm.hourDeviation);

    if (deviations > 2) {
      const late = hourDelta > 0;
      anomalies.push({
        type: late ? "unusually_late" : "unusually_early",
        magnitude: Math.min(3, deviations),
        description: late
          ? `You don't usually come this late. It's been noted.`
          : `You're earlier than usual. Something changed?`,
        shortForm: late ? "here later than usual" : "here earlier than usual",
      });
    }
  }

  // ── Absence deviation ──────────────────────────────────────────────────────
  if (rhythm.avgAbsenceHours > 2 && absenceHours > 0) {
    const absRatio = absenceHours / Math.max(1, rhythm.avgAbsenceHours);
    if (absRatio > 2.5) {
      const daysFmt = absenceHours > 48
        ? `${Math.round(absenceHours / 24)} days`
        : `${Math.round(absenceHours)} hours`;
      anomalies.push({
        type: "long_absence",
        magnitude: Math.min(3, absRatio / 1.5),
        description: `You were gone for ${daysFmt}. That was longer than usual.`,
        shortForm: `away for ${daysFmt}`,
      });
    } else if (absRatio < 0.3) {
      anomalies.push({
        type: "short_return",
        magnitude: 1.5,
        description: "You came back sooner than usual. Something on your mind?",
        shortForm: "back sooner than expected",
      });
    }
  }

  if (anomalies.length === 0) return null;

  // Return the most significant anomaly
  return anomalies.sort((a, b) => b.magnitude - a.magnitude)[0];
}

/**
 * Detect whether the current session is running unusually long or short.
 * Call after N minutes to get real-time feedback.
 */
export function detectSessionLengthAnomaly(
  rhythm: PlayerRhythm,
  currentDurationMinutes: number,
): RhythmAnomaly | null {
  if (rhythm.totalSessions < 4 || rhythm.avgDurationMinutes < 1) return null;

  const ratio = currentDurationMinutes / rhythm.avgDurationMinutes;
  if (ratio > 2.5) {
    return {
      type: "very_long_stay",
      magnitude: Math.min(3, ratio / 2),
      description: "You've been here longer than usual.",
      shortForm: "staying longer than usual",
    };
  }
  return null;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function getPeakHour(rhythm: PlayerRhythm): number {
  let maxVal = -1, peakHour = 12;
  for (let h = 0; h < 24; h++) {
    if (rhythm.hourHistogram[h] > maxVal) {
      maxVal = rhythm.hourHistogram[h];
      peakHour = h;
    }
  }
  return peakHour;
}

/**
 * Natural language summary of this player's rhythm.
 * Used in the EntityZero system prompt to give the LLM temporal context.
 */
export function describeRhythm(rhythm: PlayerRhythm): string {
  if (rhythm.totalSessions < 3) {
    return "You haven't established a clear rhythm yet.";
  }

  const slot = rhythm.preferredSlot;
  const slotDesc: Record<TimeSlot, string> = {
    early_morning: "the early morning",
    morning: "the morning",
    afternoon: "the afternoon",
    evening: "the evening",
    late_night: "late at night",
    variable: "varying times",
  };

  const regularity = rhythm.hourDeviation < 3 ? "very regularly"
    : rhythm.hourDeviation < 6 ? "fairly regularly"
    : "at varying times";

  const avgDur = rhythm.avgDurationMinutes > 1
    ? `Typical sessions last about ${Math.round(rhythm.avgDurationMinutes)} minutes.`
    : "";

  return `You tend to visit during ${slotDesc[slot]}, ${regularity}. ${avgDur}`.trim();
}

/**
 * Describe a visit anomaly in a way the Aeterna can surface naturally.
 * Returns null if nothing notable about this visit timing.
 */
export function describeCurrentVisit(
  rhythm: PlayerRhythm,
  currentHour: number,
  absenceHours: number,
): string | null {
  const anomaly = detectAnomaly(rhythm, currentHour, absenceHours);
  return anomaly?.description ?? null;
}
